import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  SuspenseQueryHookOptions,
  UseSuspenseQueryResult,
} from '@apollo/client';

import { getError, getTypeOrNull } from './apollo';
import { useEventCallback } from './useEventCallback';
import { StandardListHookOptions } from './withStandardListHook';

type QueryVariables = {
  filter?: string | null;
  pageSize?: number | null;
  pageToken?: string | null;
  orderBy?: string | null;
};

export type StandardListSuspenseHookOptions<TCustomVars = QueryVariables> = {
  filter?: string;
  pageSize?: number;
  orderBy?: string;
  skip?: boolean;
} & TCustomVars;

type ListResponse<TItem, TPath extends string> = {
  [K in TPath]: TItem[];
} & {
  nextPageToken: string | null;
};

type StandardListSuspenseHookResult<TItem, TPath extends string> = {
  [K in TPath]: TItem[];
} & {
  hasNextPage: boolean;
  nextPage: () => Promise<void>;
  startFetching: () => void;
  stopFetching: () => void;
  isEmpty: boolean;
};

// Overload for without subscription
export function withStandardListSuspenseHook<
  /* eslint-disable @typescript-eslint/no-explicit-any -- TData/TCustomVars accept Apollo-generated types whose Variables shapes don't satisfy a stricter Record<string, unknown> constraint */
  TData extends Record<string, any>,
  TItem,
  TPath extends Extract<keyof TData, string>,
  TCustomVars extends Record<string, any> = QueryVariables,
  /* eslint-enable @typescript-eslint/no-explicit-any */
>(
  useSuspenseQuery: (
    options?: SuspenseQueryHookOptions<
      TData | undefined,
      QueryVariables & TCustomVars
    >,
  ) => UseSuspenseQueryResult<TData | undefined, QueryVariables & TCustomVars>,
  dataPath: TPath,
): (
  options: StandardListHookOptions<TCustomVars>,
) => StandardListSuspenseHookResult<TItem, TPath>;

// Implementation
export function withStandardListSuspenseHook<
  /* eslint-disable @typescript-eslint/no-explicit-any -- TData/TCustomVars accept Apollo-generated types whose Variables shapes don't satisfy a stricter Record<string, unknown> constraint */
  TData extends Record<string, any>,
  TItem,
  TPath extends Extract<keyof TData, string>,
  TCustomVars extends Record<string, any> = QueryVariables,
  /* eslint-enable @typescript-eslint/no-explicit-any */
>(
  useSuspenseQuery: (
    options?: SuspenseQueryHookOptions<
      TData | undefined,
      QueryVariables & TCustomVars
    >,
  ) => UseSuspenseQueryResult<TData | undefined, QueryVariables & TCustomVars>,
  dataPath: TPath,
) {
  return (
    options: StandardListHookOptions<TCustomVars>,
  ): StandardListSuspenseHookResult<TItem, TPath> => {
    const { filter, pageSize, orderBy, skip, ...customVars } = options;
    const [isFetching, setIsFetching] = useState(false);

    const {
      data,
      error: networkError,
      fetchMore,
    } = useSuspenseQuery({
      variables: {
        filter: filter ?? null,
        pageSize: pageSize ?? null,
        orderBy: orderBy ?? null,
        ...(customVars as TCustomVars),
      },
      skip,
    });

    // Get the nested data using the provided path
    const nestedData = data?.[dataPath];
    const typedData = getTypeOrNull(nestedData) as ListResponse<
      TItem,
      TPath
    > | null;
    const appError = getError(nestedData) ?? undefined;
    const nextPageToken = typedData?.nextPageToken;

    const handleNextPage = useEventCallback(async () => {
      if (nextPageToken && fetchMore) {
        setTimeout(() => {
          startTransition(() => {
            fetchMore({
              // @ts-expect-error pageToken is not in the generic query variables type
              variables: {
                pageToken: nextPageToken,
              },
            });
          });
        });
      }
    });

    useEffect(() => {
      if (nextPageToken && isFetching) {
        handleNextPage();
      }
    }, [isFetching, nextPageToken, handleNextPage]);

    const startFetching = useCallback(() => setIsFetching(true), []);
    const stopFetching = useCallback(() => setIsFetching(false), []);

    const items = useMemo(() => typedData?.[dataPath] ?? [], [typedData]);
    const isEmpty = items.length === 0 && !appError && !networkError;

    const error = appError ?? networkError;
    if (error) throw error;

    return {
      [dataPath]: items,
      hasNextPage: !!typedData?.nextPageToken,
      nextPage: handleNextPage,
      isEmpty,
      startFetching,
      stopFetching,
    } as StandardListSuspenseHookResult<TItem, TPath>;
  };
}
